/** AttendanceInsightsTab — HR/manager attendance analytics + raw log
 *
 * Consolidates all attendance intelligence under one tab: 3-level rollups
 * (Company / Department / Individual), 3 granularities (Daily / Monthly / Yearly),
 * line charts, drill-downs, and the raw check-in/out log with full punch metadata.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtWorkspaceTime } from "@/lib/wsTime";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, MapPin, Wifi, Monitor, RefreshCw, ChevronDown, ChevronRight, Sparkles, FileDown, Loader2, ShieldCheck, PlayCircle } from "lucide-react";
import { EmployeeName } from "@/lib/employeeProfile";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar,
} from "recharts";

const GRANULARITY_LABEL = { daily: "Daily", monthly: "Monthly", yearly: "Yearly" };

function defaultRange(granularity) {
  const today = new Date();
  const to = today.toISOString().slice(0, 10);
  const from = new Date(today);
  if (granularity === "yearly") from.setFullYear(today.getFullYear() - 4);
  else if (granularity === "monthly") from.setMonth(today.getMonth() - 11);
  else from.setDate(today.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to };
}

export default function AttendanceInsightsTab({ wsId }) {
  const [scope, setScope] = useState("company");
  const [scopeId, setScopeId] = useState("");
  const [granularity, setGranularity] = useState("daily");
  const [range, setRange] = useState(() => defaultRange("daily"));
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subtab, setSubtab] = useState("attendance");

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    api.get(`/workspaces/${wsId}/departments`).then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, [wsId]);

  useEffect(() => { setRange(defaultRange(granularity)); }, [granularity]);

  const filters = { scope, scopeId, granularity, range };
  const scopeReady = scope === "company" || Boolean(scopeId);

  return (
    <div className="space-y-3" data-testid="attendance-insights">
      {/* Shared filter bar */}
      <div className="flex flex-wrap items-center gap-2 border p-3">
        <Select value={scope} onValueChange={(v) => { setScope(v); setScopeId(""); }}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="ai-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="department">Department</SelectItem>
            <SelectItem value="user">Individual</SelectItem>
          </SelectContent>
        </Select>
        {scope === "department" && (
          <Select value={scopeId} onValueChange={setScopeId}>
            <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="ai-dept-select"><SelectValue placeholder="Pick department…" /></SelectTrigger>
            <SelectContent>
              {departments.length === 0 && <div className="p-2 text-xs text-muted-foreground">No departments configured.</div>}
              {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {scope === "user" && (
          <Select value={scopeId} onValueChange={setScopeId}>
            <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="ai-user-select"><SelectValue placeholder="Pick employee…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {members.filter((m) => m.user_id).map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || m.user_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">Granularity</span>
        <Select value={granularity} onValueChange={setGranularity}>
          <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid="ai-granularity-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(GRANULARITY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-8 w-36 rounded-none text-xs" data-testid="ai-from" />
        <span className="text-xs text-muted-foreground">→</span>
        <Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-8 w-36 rounded-none text-xs" data-testid="ai-to" />
        <ExportButtons wsId={wsId} filters={filters} />
      </div>

      <Tabs value={subtab} onValueChange={setSubtab} className="w-full">
        <TabsList className="rounded-none flex-wrap h-auto justify-start" data-testid="ai-subtabs">
          <TabsTrigger value="attendance" className="rounded-none" data-testid="ai-subtab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves" className="rounded-none" data-testid="ai-subtab-leaves">Leaves</TabsTrigger>
          <TabsTrigger value="wfh" className="rounded-none" data-testid="ai-subtab-wfh">WFH</TabsTrigger>
          <TabsTrigger value="intelligence" className="rounded-none" data-testid="ai-subtab-intel"><Sparkles className="w-3.5 h-3.5 mr-1" />Intelligence</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance" className="mt-4"><AttendancePane wsId={wsId} filters={filters} scopeReady={scopeReady} /></TabsContent>
        <TabsContent value="leaves" className="mt-4"><LeavesPane wsId={wsId} filters={filters} scopeReady={scopeReady} /></TabsContent>
        <TabsContent value="wfh" className="mt-4"><WfhPane wsId={wsId} filters={filters} scopeReady={scopeReady} /></TabsContent>
        <TabsContent value="intelligence" className="mt-4"><IntelPane wsId={wsId} filters={filters} scopeReady={scopeReady} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────── Export buttons ───────────────────

function ExportButtons({ wsId, filters }) {
  const { scope, scopeId, range } = filters;
  const [busy, setBusy] = useState(null);
  const buildUrl = (ext) => {
    const p = new URLSearchParams({ from: range.from, to: range.to, scope });
    if (scopeId) p.set("scope_id", scopeId);
    return `/workspaces/${wsId}/attendance/export.${ext}?${p.toString()}`;
  };
  const download = async (ext) => {
    setBusy(ext);
    try {
      const res = await api.get(buildUrl(ext), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${range.from}_${range.to}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, `${ext.toUpperCase()} export failed`)); }
    finally { setBusy(null); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8 ml-auto" onClick={() => download("csv")} disabled={!!busy} data-testid="ai-export-csv">
        {busy === "csv" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}CSV
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => download("pdf")} disabled={!!busy} data-testid="ai-export-pdf">
        {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}PDF
      </Button>
    </>
  );
}

// ─────────────────── Panes ───────────────────

function useAnalytics(url, params, ready) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!ready) { setData(null); return; }
    setBusy(true); setErr(null);
    api.get(`${url}?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed"))
      .finally(() => setBusy(false));
  }, [url, params, ready]);
  return { data, busy, err };
}

function paramsOf(filters) {
  const { scope, scopeId, granularity, range } = filters;
  const p = new URLSearchParams({ scope, granularity, from: range.from, to: range.to });
  if (scopeId && scope !== "company") p.set("scope_id", scopeId);
  return p.toString();
}

function AttendancePane({ wsId, filters, scopeReady }) {
  const params = paramsOf(filters);
  const { data, busy, err } = useAnalytics(`/workspaces/${wsId}/attendance/analytics`, params, scopeReady && !!wsId);
  const [rawOpen, setRawOpen] = useState(true);
  if (!scopeReady) return <ScopePrompt />;
  if (err) return <ErrorBox err={err} />;
  if (!data) return <div className="text-sm text-muted-foreground p-6">{busy ? "Loading…" : "No data"}</div>;
  return (
    <div className="space-y-4">
      <SummaryTiles data={data} />
      <ChartCard data={data} granularity={filters.granularity} />
      <DrillDown data={data} />
      <div className="border">
        <button type="button" className="w-full px-3 py-2 border-b text-left text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-2 hover:bg-muted/30" onClick={() => setRawOpen((v) => !v)} data-testid="ai-raw-toggle">
          {rawOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Raw check-in / check-out log
        </button>
        {rawOpen && <RawLog range={filters.range} scope={filters.scope} scopeId={filters.scopeId} />}
      </div>
    </div>
  );
}

function LeavesPane({ wsId, filters, scopeReady }) {
  const p = paramsOf({ ...filters, granularity: filters.granularity === "daily" ? "monthly" : filters.granularity });
  const { data, busy, err } = useAnalytics(`/workspaces/${wsId}/leaves/analytics`, p, scopeReady && !!wsId);
  if (!scopeReady) return <ScopePrompt />;
  if (err) return <ErrorBox err={err} />;
  if (!data) return <div className="text-sm text-muted-foreground p-6">{busy ? "Loading…" : "No data"}</div>;
  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="leaves-insights">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Tile label="Total leave days" value={s.total_days} cls="text-sky-600" />
        <Tile label="Approved requests" value={s.requests} />
        <Tile label="Headcount" value={data.headcount} />
        <Tile label="Types" value={(data.by_type || []).length} />
      </div>
      <div className="border p-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Leave days over time</div>
        <div className="h-64" data-testid="leaves-chart">
          <ResponsiveContainer>
            <BarChart data={data.points || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="paid_days" stackId="a" fill="#0ea5e9" name="Paid" />
              <Bar dataKey="unpaid_days" stackId="a" fill="#64748b" name="Unpaid" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border">
          <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Breakdown by type</div>
          {(data.by_type || []).length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No leave taken.</div>}
          {(data.by_type || []).map((t) => (
            <div key={t.code} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
              <Badge variant="outline" className="rounded-none text-[9px]">{t.code}</Badge>
              <span className="flex-1 truncate">{t.name}</span>
              <span className="font-mono font-bold">{t.days}d</span>
              <span className="text-muted-foreground">{t.requests}×</span>
            </div>
          ))}
        </div>
        <div className="border">
          <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Top users</div>
          {(data.top_users || []).length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No records.</div>}
          {(data.top_users || []).map((u) => (
            <div key={u.user_id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
              <EmployeeName userId={u.user_id} name={u.name} className="flex-1 truncate font-semibold" />
              <span className="text-muted-foreground truncate">{u.department}</span>
              <span className="font-mono font-bold">{u.days}d</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WfhPane({ wsId, filters, scopeReady }) {
  const p = paramsOf({ ...filters, granularity: filters.granularity === "daily" ? "monthly" : filters.granularity });
  const { data, busy, err } = useAnalytics(`/workspaces/${wsId}/wfh/analytics`, p, scopeReady && !!wsId);
  if (!scopeReady) return <ScopePrompt />;
  if (err) return <ErrorBox err={err} />;
  if (!data) return <div className="text-sm text-muted-foreground p-6">{busy ? "Loading…" : "No data"}</div>;
  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="wfh-insights">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Tile label="Total WFH days" value={s.total_days} cls="text-violet-600" />
        <Tile label="Requests" value={s.requests} />
        <Tile label="Headcount" value={data.headcount} />
      </div>
      <div className="border p-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">WFH days over time</div>
        <div className="h-64" data-testid="wfh-chart">
          <ResponsiveContainer>
            <BarChart data={data.points || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} />
              <Bar dataKey="days" fill="#8b5cf6" name="WFH days" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="border">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Top users</div>
        {(data.top_users || []).length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No records.</div>}
        {(data.top_users || []).map((u) => (
          <div key={u.user_id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <EmployeeName userId={u.user_id} name={u.name} className="flex-1 truncate font-semibold" />
            <span className="text-muted-foreground truncate">{u.department}</span>
            <span className="font-mono font-bold">{u.days}d</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntelPane({ wsId, filters, scopeReady }) {
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [rules, setRules] = useState(null);
  const [runResult, setRunResult] = useState(null);

  const loadHistory = () => {
    if (!scopeReady || !wsId) return;
    const p = new URLSearchParams({ from: filters.range.from, to: filters.range.to, limit: "20" });
    if (filters.scope === "user" && filters.scopeId) p.set("user_id", filters.scopeId);
    api.get(`/workspaces/${wsId}/attendance/compliance-history?${p.toString()}`)
      .then((r) => setHist(r.data)).catch(() => setHist(null));
  };
  useEffect(() => {
    loadHistory();
    api.get(`/workspaces/${wsId}/attendance/compliance-rules`).then((r) => setRules(r.data)).catch(() => {});
  }, [wsId, filters.range.from, filters.range.to, filters.scope, filters.scopeId]);

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const { data: d } = await api.post(`/workspaces/${wsId}/attendance/ai-narrative`, {
        from: filters.range.from, to: filters.range.to,
        scope: filters.scope, scope_id: filters.scopeId || null,
      });
      setAi(d);
    } catch (e) { toast.error(apiErrorMessage(e, "AI narrative failed")); }
    finally { setAiBusy(false); }
  };

  const runCompliance = async () => {
    setRunBusy(true);
    try {
      const { data: d } = await api.post(`/workspaces/${wsId}/attendance/compliance-run`, {
        from: filters.range.from, to: filters.range.to,
        scope: filters.scope, scope_id: filters.scopeId || null, persist: true,
      });
      setRunResult(d);
      toast.success(`Compliance scan: ${d.count} violation${d.count !== 1 ? "s" : ""} recorded`);
      loadHistory();
    } catch (e) { toast.error(apiErrorMessage(e, "Compliance run failed")); }
    finally { setRunBusy(false); }
  };

  if (!scopeReady) return <ScopePrompt />;

  return (
    <div className="space-y-4" data-testid="intel-pane">
      <div className="border p-4 space-y-3" data-testid="intel-ai-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3" /> Combined AI narrative</div>
            <div className="text-xs text-muted-foreground mt-0.5">Fuses attendance · leaves · WFH · compliance for {filters.range.from} → {filters.range.to}</div>
          </div>
          <Button size="sm" variant="outline" className="rounded-none" onClick={generateAI} disabled={aiBusy} data-testid="intel-ai-generate">
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {aiBusy ? "Generating…" : (ai ? "Refresh" : "Generate")}
          </Button>
        </div>
        {ai && (
          <div className="border border-violet-500/30 bg-violet-500/5 p-3 space-y-2">
            <div className="font-bold text-sm">{ai.headline}</div>
            <p className="text-sm">{ai.narrative}</p>
            {(ai.risks || []).length > 0 && (
              <div><div className="text-[10px] uppercase text-red-600">Risks</div><ul className="list-disc pl-5 text-xs">{ai.risks.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
            )}
            {(ai.opportunities || []).length > 0 && (
              <div><div className="text-[10px] uppercase text-emerald-600">Opportunities</div><ul className="list-disc pl-5 text-xs">{ai.opportunities.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
            )}
            {(ai.recommendations || []).length > 0 && (
              <div><div className="text-[10px] uppercase text-violet-600">Recommendations</div><ul className="list-disc pl-5 text-xs">{ai.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
            )}
          </div>
        )}
      </div>

      <div className="border p-4 space-y-3" data-testid="intel-compliance-card">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Compliance history</div>
          <Button size="sm" variant="outline" className="rounded-none" onClick={runCompliance} disabled={runBusy} data-testid="intel-compliance-run">
            {runBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}Run scan now
          </Button>
        </div>
        {runResult && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs" data-testid="intel-run-result">
            Scan complete: <b>{runResult.count}</b> violation{runResult.count !== 1 ? "s" : ""}. Breakdown: {Object.entries(runResult.by_kind || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}
          </div>
        )}
        {hist && hist.total > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="intel-hist-breakdown">
            {Object.entries(hist.by_kind || {}).map(([k, v]) => (
              <Badge key={k} variant="outline" className="rounded-none text-[10px]"><AlertTriangle className="w-3 h-3 mr-1 text-orange-500" />{hist.kind_labels?.[k] || k} · {v}</Badge>
            ))}
          </div>
        )}
        <div className="border" data-testid="intel-hist-table">
          <div className="px-3 py-1.5 border-b text-[10px] uppercase text-muted-foreground">Recent violations ({hist?.total || 0})</div>
          {(!hist || hist.rows.length === 0) && <div className="p-4 text-xs text-muted-foreground text-center">No violations recorded — run a scan to populate.</div>}
          {(hist?.rows || []).map((v) => (
            <div key={v.id} className="border-t px-3 py-1.5 flex items-center gap-2 text-xs" data-testid={`intel-hist-row-${v.id}`}>
              <EmployeeName userId={v.user_id} name={v.user_name} className="min-w-32 truncate" />
              <span className="text-muted-foreground truncate">{v.department}</span>
              <span className="font-mono">{v.date}</span>
              <Badge variant="outline" className={`rounded-none text-[9px] ${v.severity === "high" ? "text-red-500" : v.severity === "medium" ? "text-orange-500" : "text-amber-500"}`}>{v.severity}</Badge>
              <span className="flex-1 truncate">{v.rule_label}</span>
              <span className="text-muted-foreground text-[10px] truncate max-w-40">{v.detail}</span>
              {v.acknowledged && <Badge variant="outline" className="rounded-none text-[9px] text-emerald-600">ACK</Badge>}
            </div>
          ))}
        </div>
      </div>

      {rules && (
        <div className="border p-4 text-xs space-y-1" data-testid="intel-rules-summary">
          <div className="text-[10px] uppercase text-muted-foreground">Active rules (from HR Settings → Compliance)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
            <div>Late grace: <b>{rules.workspace.late_grace_minutes}m</b></div>
            <div>Min hours: <b>{rules.workspace.min_hours_per_day}h</b></div>
            <div>Missed check-in flag: <b>{rules.workspace.flag_missed_checkin ? "on" : "off"}</b></div>
            <div>Anomaly flag: <b>{rules.workspace.flag_anomalies ? "on" : "off"}</b></div>
            <div>Geofence: <b>{rules.workspace.geofence_required ? "required" : "off"}</b></div>
            <div>Dept. overrides: <b>{rules.department_overrides.length}</b></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, cls }) {
  return (
    <div className="border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono font-black ${cls || ""}`}>{value ?? "—"}</div>
    </div>
  );
}
function ScopePrompt() { return <div className="border p-8 text-center text-sm text-muted-foreground">Pick a scope entity to view analytics.</div>; }
function ErrorBox({ err }) { return <div className="border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="ai-error">{err}</div>; }

function SummaryTiles({ data }) {
  const s = data.summary || {};
  const tiles = [
    { l: "Head count", v: s.headcount },
    { l: "Present", v: s.present, cls: "text-emerald-600" },
    { l: "Half day", v: s.half_day, cls: "text-amber-600" },
    { l: "Leave", v: s.leave, cls: "text-sky-600" },
    { l: "WFH", v: s.wfh, cls: "text-violet-600" },
    { l: "Absent (est.)", v: s.absent, cls: "text-red-600" },
    { l: "Late incidence", v: `${s.late_incidence_pct}%` },
    { l: "Records", v: s.attendance_records },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ai-summary-tiles">
      {tiles.map((t) => (
        <div key={t.l} className="border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.l}</div>
          <div className={`text-2xl font-mono font-black ${t.cls || ""}`}>{t.v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ data, granularity }) {
  const points = (data.points || []).map((p) => ({ ...p, bucket: p.bucket }));
  return (
    <div className="border p-3" data-testid="ai-chart-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Trend — {GRANULARITY_LABEL[granularity]}</div>
        <div className="text-[10px] text-muted-foreground">{data.from} → {data.to}</div>
      </div>
      <div className="h-72" data-testid="ai-line-chart">
        <ResponsiveContainer>
          <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} tickFormatter={(b) => granularity === "daily" ? b.slice(5) : b} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} dot={false} name="Present" />
            <Line type="monotone" dataKey="half_day" stroke="#f59e0b" strokeWidth={2} dot={false} name="Half day" />
            <Line type="monotone" dataKey="leave" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Leave" />
            <Line type="monotone" dataKey="wfh" stroke="#8b5cf6" strokeWidth={2} dot={false} name="WFH" />
            {granularity === "daily" && <Line type="monotone" dataKey="absent" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Absent (est.)" />}
            <Line type="monotone" dataKey="avg_late_minutes" stroke="#f97316" strokeWidth={1.5} dot={false} name="Avg late (min)" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DrillDown({ data }) {
  const late = data.top_late || [];
  const absent = (data.top_absent || []).filter((u) => (u.records || 0) === 0 || u.present === 0);
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="ai-drilldown">
      <div className="border">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Top late performers</div>
        {late.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No lateness recorded.</div>}
        {late.map((u) => (
          <div key={u.user_id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <EmployeeName userId={u.user_id} name={u.name} className="flex-1 truncate font-semibold" />
            <span className="text-muted-foreground truncate">{u.department}</span>
            <span className="font-mono text-orange-600">{u.avg_late_minutes}m avg</span>
            <span className="font-mono text-muted-foreground">{u.late_count}×</span>
          </div>
        ))}
      </div>
      <div className="border">
        <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">Least attendance activity</div>
        {absent.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">All members attended.</div>}
        {absent.slice(0, 10).map((u) => (
          <div key={u.user_id} className="border-t px-3 py-2 flex items-center gap-2 text-xs">
            <EmployeeName userId={u.user_id} name={u.name} className="flex-1 truncate font-semibold" />
            <span className="text-muted-foreground truncate">{u.department}</span>
            <span className="font-mono">{u.present}p</span>
            <span className="font-mono text-sky-600">{u.leave}L</span>
            <span className="font-mono text-violet-600">{u.wfh}WFH</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RawLog({ range, scope, scopeId }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [anomOnly, setAnomOnly] = useState(false);
  const limit = 25;

  const params = useMemo(() => {
    const p = new URLSearchParams({ from: range.from, to: range.to,
                                     limit: String(limit), offset: String(offset) });
    if (anomOnly) p.set("has_anomaly", "true");
    if (scope === "user" && scopeId) p.set("user_id", scopeId);
    if (scope === "department" && scopeId) p.set("department", scopeId);
    return p.toString();
  }, [range, offset, anomOnly, scope, scopeId]);

  useEffect(() => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/attendance/raw-log?${params}`)
      .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, [wsId, params]);

  return (
    <div className="p-3 space-y-2" data-testid="ai-raw-log">
      <div className="flex items-center gap-2 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={anomOnly} onChange={(e) => { setAnomOnly(e.target.checked); setOffset(0); }} data-testid="ai-raw-anom-toggle" />
          <span>Anomalies only</span>
        </label>
        <span className="ml-auto text-[11px] text-muted-foreground">{total} record{total !== 1 ? "s" : ""}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-muted/40 text-left text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5">Person</th>
              <th className="px-3 py-1.5">Date</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5">In</th>
              <th className="px-3 py-1.5">Out</th>
              <th className="px-3 py-1.5">Hours</th>
              <th className="px-3 py-1.5">Late</th>
              <th className="px-3 py-1.5">Location</th>
              <th className="px-3 py-1.5">IP · Device</th>
              <th className="px-3 py-1.5">Anom.</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">{busy ? "Loading…" : "No records."}</td></tr>
            )}
            {rows.map((r) => {
              const meta = r.check_in_meta || {};
              const person = r._person || { name: "—" };
              return (
                <tr key={r.id || `${r.user_id}-${r.date}`} className="border-t" data-testid={`ai-raw-row-${r.id || r.date}`}>
                  <td className="px-3 py-1.5"><EmployeeName userId={r.user_id} name={person.name} /><div className="text-[10px] text-muted-foreground">{person.department}</div></td>
                  <td className="px-3 py-1.5 font-mono">{r.date}</td>
                  <td className="px-3 py-1.5">{r.status || "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{r.clock_in ? fmtWorkspaceTime(r.clock_in) : "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{r.clock_out ? fmtWorkspaceTime(r.clock_out) : "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{r.work_minutes ? (r.work_minutes / 60).toFixed(1) : "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{r.late_minutes ? <span className="text-red-500">+{r.late_minutes}m</span> : "—"}</td>
                  <td className="px-3 py-1.5 max-w-40 truncate text-muted-foreground">
                    {meta.lat != null ? (
                      <a href={`https://www.google.com/maps?q=${meta.lat},${meta.lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                        <MapPin className="w-3 h-3" />{meta.address ? String(meta.address).slice(0, 32) : `${meta.lat.toFixed(3)}, ${meta.lng.toFixed(3)}`}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-muted-foreground">
                    {meta.ip && <div className="inline-flex items-center gap-1"><Wifi className="w-3 h-3" />{meta.ip}</div>}
                    {meta.device_type && <div className="inline-flex items-center gap-1"><Monitor className="w-3 h-3" />{meta.device_type} · {meta.browser || "?"}</div>}
                  </td>
                  <td className="px-3 py-1.5">
                    {(r.anomalies || []).map((a) => (
                      <Badge key={a} variant="outline" className="rounded-none border-orange-500/40 text-orange-600 text-[9px] mr-1 inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />{a.replace(/_/g, " ")}</Badge>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Button size="sm" variant="outline" className="rounded-none h-7" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="ai-raw-prev">Prev</Button>
        <span className="text-muted-foreground">Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
        <Button size="sm" variant="outline" className="rounded-none h-7" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="ai-raw-next">Next</Button>
      </div>
    </div>
  );
}
